import React, { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { App as AntApp } from "antd";

import { useAuthStore } from "./store/authStore";
import { authService } from "./services/authService";
import LoginPage from "./pages/LoginPage";
import ProtectedRoute from "./components/Auth/ProtectedRoute";
import MainLayout from "./components/Layout/MainLayout";
import DashboardPage from "./pages/DashboardPage";
import CustomersPage from "./pages/CustomersPage";
import CoursesPage from "./pages/CoursesPage";
import BookingsPage from "./pages/BookingsPage";
import ReportsPage from "./pages/ReportsPage";
import RoomsPage from "./pages/RoomsPage";
import EmployeesPage from "./pages/EmployeesPage";
import ServicesPage from "./pages/ServicesPage";
import ReceiptsPage from "./pages/ReceiptsPage";
import CustomerCoursesPage from "./pages/CustomerCoursesPage";
import ServiceRecordsPage from "./pages/ServiceRecordsPage";
import CommissionsPage from "./pages/CommissionsPage";
import ExchangeRatesPage from "./pages/ExchangeRatesPage";
import ServiceBonusPage from "./pages/ServiceBonusPage";
import ServiceChargePage from "./pages/ServiceChargePage";
import ReportsServiceChargePage from "./pages/ReportsServiceChargePage";
import AttendanceManagementPage from "./pages/AttendanceManagementPage";
import EmployeeAttendancePage from "./pages/EmployeeAttendancePage";
import SalariesPage from "./pages/SalariesPage";
import ReportsDocumentPage from "./pages/ReportsDocumentPage";
import PublicMenuPage from "./pages/PublicMenuPage";
import PromotionsPage from "./pages/Promotionspage";

function App() {
  const { isAuthenticated, user, login, logout } = useAuthStore();
  const [isLoading, setIsLoading] = React.useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem("token");

      if (token && !user) {
        try {
          const response = await authService.getMe();
          if (response.success) {
            login(response.data, token);
          } else {
            logout();
          }
        } catch {
          logout();
        }
      }

      setIsLoading(false); // ✅ เสร็จแล้วค่อยให้ render
    };

    initAuth();
  }, []); // ✅ ลบ dependencies ออก เพราะจะทำแค่ครั้งแรก

    // ✅ แสดง loading screen ขณะตรวจสอบ auth
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        ກຳລັງໂຫລດ...
      </div>
    );
  }

  // Function to wrap pages with MainLayout
  const withLayout = (page: React.ReactNode) => (
    <ProtectedRoute>
      <MainLayout>{page}</MainLayout>
    </ProtectedRoute>
  );

  // Function for pages without MainLayout (Employee Attendance)
  const withoutLayout = (page: React.ReactNode) => (
    <ProtectedRoute>{page}</ProtectedRoute>
  );

  // ✅ ກຳນົດ Default Route ຕາມ Role
  const getDefaultRoute = () => {
    if (!user) return "/";

    // Therapist ໃຫ້ໄປໜ້າບັນທຶກເວລາ
    if (user.role === "therapist") {
      return "/attendance/employee";
    }

    // Admin ແລະ Cashier ໃຫ້ໄປ Dashboard
    return "/";
  };

  return (
    <AntApp>
      <Routes>
        {/* Login Route */}
        <Route path="/menu" element={<PublicMenuPage />} />
        <Route
          path="/login"
          element={
            isAuthenticated ? (
              <Navigate to={getDefaultRoute()} replace />
            ) : (
              <LoginPage />
            )
          }
        />

        {/* ==================== EMPLOYEE ATTENDANCE (ບໍ່ມີ Layout) ==================== */}
        <Route
          path="/attendance/employee"
          element={withoutLayout(<EmployeeAttendancePage />)}
        />

        {/* ==================== DASHBOARD ==================== */}
        <Route
          path="/"
          element={
            user?.role === "therapist" ? (
              <Navigate to="/attendance/employee" replace />
            ) : (
              withLayout(<DashboardPage />)
            )
          }
        />

        {/* ==================== CUSTOMER ROUTES ==================== */}
        <Route path="/customers" element={withLayout(<CustomersPage />)} />

        {/* ==================== COURSE/SERVICE ROUTES ==================== */}
        <Route path="/services" element={withLayout(<ServicesPage />)} />
        <Route path="/courses" element={withLayout(<CoursesPage />)} />
        <Route path="/promotions" element={withLayout(<PromotionsPage />)} />
        <Route path="/receipts" element={withLayout(<ReceiptsPage />)} />
        <Route
          path="/customer-courses"
          element={withLayout(<CustomerCoursesPage />)}
        />
        <Route
          path="/service-records"
          element={withLayout(<ServiceRecordsPage />)}
        />
        <Route
          path="/service-charge"
          element={withLayout(<ServiceChargePage />)}
        />
        <Route path="/commissions" element={withLayout(<CommissionsPage />)} />
        <Route
          path="/service-charge-report"
          element={withLayout(<ReportsServiceChargePage />)}
        />

        {/* ==================== BOOKING ROUTES ==================== */}
        <Route path="/bookings" element={withLayout(<BookingsPage />)} />

        {/* ==================== REPORTS ROUTES ==================== */}
        <Route path="/reports" element={withLayout(<ReportsPage />)} />
        <Route
          path="/reports-customer"
          element={withLayout(<ReportsDocumentPage />)}
        />

        {/* ==================== ADMIN/CASHIER ONLY ROUTES ==================== */}
        {(user?.role === "admin" || user?.role === "cashier") && (
          <>
            <Route
              path="/attendance"
              element={withLayout(<AttendanceManagementPage />)}
            />
            <Route path="/salaries" element={withLayout(<SalariesPage />)} />

            <Route path="/employees" element={withLayout(<EmployeesPage />)} />
            <Route path="/rooms" element={withLayout(<RoomsPage />)} />
            <Route
              path="/service-bonus"
              element={withLayout(<ServiceBonusPage />)}
            />
            <Route
              path="/exchanges"
              element={withLayout(<ExchangeRatesPage />)}
            />
          </>
        )}

        {/* ==================== CATCH ALL ==================== */}
        <Route path="*" element={<Navigate to={getDefaultRoute()} replace />} />
      </Routes>
    </AntApp>
  );
}

export default App;
